export interface GameSettings {
  mouseSensitivity: number;
  masterVolume: number;
  effectsVolume: number;
  ambienceVolume: number;
  cameraBob: boolean;
  touchSensitivity: number;
  quality: "auto" | "low" | "balanced" | "high";
}

export const DEFAULT_SEED = "INFERNAL-BREACH-01";

export const MONSTERS_ENABLED = true;

export const DEFAULT_SETTINGS: GameSettings = {
  mouseSensitivity: 0.0018,
  masterVolume: 0.8,
  effectsVolume: 0.85,
  ambienceVolume: 0.25,
  cameraBob: true,
  touchSensitivity: 1,
  quality: "auto",
};

export const PLAYER_CONFIG = {
  maximumHealth: 100,
  cameraHeight: 1.65,
  collisionRadius: 0.35,
  walkSpeed: 5,
  sprintSpeed: 7,
  acceleration: 24,
  deceleration: 30,
  interactionRange: 3.2,
} as const;

export const STORAGE_KEYS = {
  settings: "infernal-breach.settings",
  activeSeed: "infernal-breach.active-seed",
} as const;

const HARDWARE_SCALING_LEVELS: Readonly<Record<GameSettings["quality"], number>> = {
  auto: 1.5,
  low: 2,
  balanced: 1.5,
  high: 1,
};

export function hardwareScalingLevelForQuality(quality: GameSettings["quality"]): number {
  return HARDWARE_SCALING_LEVELS[quality];
}

export function loadSettings(): GameSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.settings);
    if (!raw) {
      return { ...DEFAULT_SETTINGS };
    }
    const stored = JSON.parse(raw) as Partial<GameSettings>;
    return {
      mouseSensitivity: clamp(
        stored.mouseSensitivity,
        0.0005,
        0.004,
        DEFAULT_SETTINGS.mouseSensitivity,
      ),
      masterVolume: clamp(stored.masterVolume, 0, 1, DEFAULT_SETTINGS.masterVolume),
      effectsVolume: clamp(stored.effectsVolume, 0, 1, DEFAULT_SETTINGS.effectsVolume),
      ambienceVolume: clamp(stored.ambienceVolume, 0, 1, DEFAULT_SETTINGS.ambienceVolume),
      cameraBob: stored.cameraBob ?? DEFAULT_SETTINGS.cameraBob,
      touchSensitivity: clamp(stored.touchSensitivity, 0.5, 2, DEFAULT_SETTINGS.touchSensitivity),
      quality: ["auto", "low", "balanced", "high"].includes(stored.quality ?? "")
        ? (stored.quality as GameSettings["quality"])
        : "auto",
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function clamp(value: number | undefined, min: number, max: number, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(min, Math.min(max, value))
    : fallback;
}
